import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, CircularProgress, FormControlLabel, Grid, IconButton, Stack, TextField as MuiTextField, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckIcon from '@mui/icons-material/Check';
import { cocCategories, cocImpactedElements } from '../../../services/mockData';
import { useToast } from '../../../components/shared/Toast';
import SectionHeader from '../../../components/shared/SectionHeader';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import loanPricingApi from '../../../services/loanPricingApi';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';

const COCRequestForm = ({ loan, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    discoveryDate: '',
    reasonNotKnown: '',
    impactedElements: [],
    supportingDocuments: [],
    attestation: false
  });

  const [errors, setErrors] = useState({});
  const { showToast, ToastContainer } = useToast();
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitCocRequest] = loanPricingApi.endpoints.submitCocRequest.useMutation();

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.category) {
          newErrors.category = 'COC category is required';
        }
        break;
      case 2:
        if (!formData.description || formData.description.trim().length < 50) {
          newErrors.description = 'Description must be at least 50 characters and answer all required questions';
        }
        if (!formData.discoveryDate) {
          newErrors.discoveryDate = 'Discovery date is required';
        }
        if (!formData.reasonNotKnown || formData.reasonNotKnown.trim().length < 30) {
          newErrors.reasonNotKnown = 'Explanation must be at least 30 characters';
        }
        break;
      case 3:
        if (formData.impactedElements.length === 0) {
          newErrors.impactedElements = 'Select at least one impacted element';
        }
        break;
      case 4:
        if (formData.supportingDocuments.length === 0) {
          newErrors.supportingDocuments = 'At least one supporting document is required';
        }
        break;
      case 5:
        if (!formData.attestation) {
          newErrors.attestation = 'Attestation is required to submit';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAll = () => {
    return validateStep(1) && validateStep(2) && validateStep(3) && validateStep(4) && validateStep(5);
  };

  const handleCategoryChange = (categoryId) => {
    setFormData({ ...formData, category: categoryId });
    setErrors({ ...errors, category: '' });
  };

  const handleImpactedElementToggle = (elementId) => {
    const current = formData.impactedElements;
    const updated = current.includes(elementId)
      ? current.filter(id => id !== elementId)
      : [...current, elementId];
    
    setFormData({ ...formData, impactedElements: updated });
    setErrors({ ...errors, impactedElements: '' });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);

    // Simulate file upload
    setTimeout(() => {
      const newDocs = files.map((file, idx) => ({
        id: Date.now() + idx,
        fileName: file.name,
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        uploadDate: new Date().toISOString(),
        label: ''
      }));

      setFormData({
        ...formData,
        supportingDocuments: [...formData.supportingDocuments, ...newDocs]
      });
      setErrors({ ...errors, supportingDocuments: '' });
      setUploadingFiles(false);
      e.target.value = '';
    }, 500);
  };

  const handleDocumentLabelChange = (docId, label) => {
    setFormData({
      ...formData,
      supportingDocuments: formData.supportingDocuments.map(doc =>
        doc.id === docId ? { ...doc, label } : doc
      )
    });
  };

  const handleRemoveDocument = (docId) => {
    setFormData({
      ...formData,
      supportingDocuments: formData.supportingDocuments.filter(doc => doc.id !== docId)
    });
  };

  const normalizeCocForEmail = (cocRequest) => {
    const selectedImpactedElements = cocRequest.impactedElements
      .map((id) => cocImpactedElements.find((x) => x.id === id)?.name || id)
      .join(', ');

    const documents = cocRequest.supportingDocuments
      .map((doc) => `${doc.fileName}${doc.label ? ` (${doc.label})` : ''}`)
      .join(', ');

    return {
      category: cocRequest.categoryName || cocRequest.category,
      description: cocRequest.description,
      discoveryDate: cocRequest.discoveryDate,
      reasonNotKnown: cocRequest.reasonNotKnown,
      impactedElements: selectedImpactedElements || '—',
      supportingDocuments: documents || '—',
      attestation: cocRequest.attestation ? 'Yes' : 'No',
      submittedBy: cocRequest.submittedBy,
      submittedDate: cocRequest.submittedDate,
    };
  };

  const handleSubmit = async () => {
    if (!validateAll()) {
      showToast('Please complete all required fields before submitting.', 'warning');
      return;
    }

    const selectedCategory = cocCategories.find(cat => cat.id === formData.category);
    
    const cocRequest = {
      id: Date.now(),
      loanId: loan?.loanId,
      loanNumber: loan?.loanNumber,
      category: formData.category,
      categoryName: selectedCategory.name,
      description: formData.description,
      discoveryDate: formData.discoveryDate,
      reasonNotKnown: formData.reasonNotKnown,
      impactedElements: formData.impactedElements,
      supportingDocuments: formData.supportingDocuments,
      attestation: formData.attestation,
      attestationText: 'I attest that this change represents a valid Change of Circumstance under TRID regulations, was not known or reasonably foreseeable at the time of initial disclosure, and is supported by the attached documentation.',
      submittedBy: 'Michael Chen', // Would come from auth context
      submittedDate: new Date().toISOString(),
      status: 'Submitted',
      reviewedBy: null,
      reviewedDate: null,
      reviewNotes: null,
      revisedDisclosureIssued: false,
      revisedDisclosureDate: null
    };

    try {
      if (!cocRequest.loanId) {
        showToast('Missing loan ID. Please refresh and try again.', 'danger');
        return;
      }

      await submitCocRequest({
        loanId: cocRequest.loanId,
        body: {
          actionKey: 'cocRequest',
          actionLabel: 'COC Request',
          formData: normalizeCocForEmail(cocRequest),
        },
      }).unwrap();

      onSubmit(cocRequest);
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Failed to submit COC request.'), 'danger');
    }
  };

  const selectedCategory = cocCategories.find(cat => cat.id === formData.category);

  return (
    <Card variant="outlined">
      {ToastContainer}
      <CardHeader title={<SectionHeader title="Request Change of Circumstance" icon={<SwapHorizIcon fontSize="small" />} sx={{ mb: 0 }} />} subheader="Submit a TRID-compliant COC request for lender review" subheaderTypographyProps={{ variant: 'caption' }} />
      <CardContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">TRID Compliance Notice</Typography>
          <Typography variant="body2" color="text.secondary">Changes of Circumstance must be valid under TRID regulations. Invalid COC types (pricing errors, broker compensation changes, margin adjustments) are not permitted. All requests require supporting documentation and will be reviewed for compliance.</Typography>
        </Alert>

        {/* Step 1 */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Chip label="1" size="small" color="primary" /><Typography variant="subtitle2">Select COC Category *</Typography>
          </Stack>
          <Grid container spacing={2}>
            {cocCategories.map(category => (
              <Grid key={category.id} size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ cursor: 'pointer', borderColor: formData.category === category.id ? 'primary.main' : undefined, borderWidth: formData.category === category.id ? 2 : 1 }} onClick={() => handleCategoryChange(category.id)}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="subtitle2">{category.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{category.description}</Typography>
                    <Typography variant="caption" color="text.disabled">{category.complianceNote}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {errors.category && <Typography variant="caption" color="error.main" sx={{ mt: 0.5 }}>{errors.category}</Typography>}
        </Box>

        {/* Step 2 */}
        {formData.category && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip label="2" size="small" color="primary" /><Typography variant="subtitle2">Change Description *</Typography>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}><strong>Required Information:</strong> Your description must clearly answer: What changed? When was it discovered? Why was it not known at initial disclosure?</Alert>
            <MuiTextField label="Detailed Description *" fullWidth multiline rows={5} size="small" placeholder="Provide a detailed explanation..." value={formData.description} error={!!errors.description} helperText={errors.description || `${formData.description.length} characters (minimum 50 required)`} onChange={(e) => { setFormData({ ...formData, description: e.target.value }); setErrors({ ...errors, description: '' }); }} sx={{ mb: 2 }} />
            <MuiTextField label="Discovery Date *" type="date" size="small" slotProps={{ htmlInput: { max: new Date().toISOString().split('T')[0] }, inputLabel: { shrink: true } }} value={formData.discoveryDate} error={!!errors.discoveryDate} helperText={errors.discoveryDate} onChange={(e) => { setFormData({ ...formData, discoveryDate: e.target.value }); setErrors({ ...errors, discoveryDate: '' }); }} sx={{ mb: 2, width: '50%' }} />
            <MuiTextField label="Why was this not known at initial disclosure? *" fullWidth multiline rows={3} size="small" placeholder="Explain why this change was not known..." value={formData.reasonNotKnown} error={!!errors.reasonNotKnown} helperText={errors.reasonNotKnown || `${formData.reasonNotKnown.length} characters (minimum 30 required)`} onChange={(e) => { setFormData({ ...formData, reasonNotKnown: e.target.value }); setErrors({ ...errors, reasonNotKnown: '' }); }} />
          </Box>
        )}

        {/* Step 3 */}
        {formData.category && formData.description && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip label="3" size="small" color="primary" /><Typography variant="subtitle2">Impacted Loan Elements *</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {cocImpactedElements.map(element => (
                <Chip key={element.id} label={element.name} size="small" icon={formData.impactedElements.includes(element.id) ? <CheckIcon /> : undefined} color={formData.impactedElements.includes(element.id) ? 'primary' : 'default'} variant={formData.impactedElements.includes(element.id) ? 'filled' : 'outlined'} onClick={() => handleImpactedElementToggle(element.id)} sx={{ mb: 0.5 }} />
              ))}
            </Stack>
            {errors.impactedElements && <Typography variant="caption" color="error.main" sx={{ mt: 0.5 }}>{errors.impactedElements}</Typography>}
          </Box>
        )}

        {/* Step 4 */}
        {formData.category && formData.impactedElements.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip label="4" size="small" color="primary" /><Typography variant="subtitle2">Supporting Documentation *</Typography>
            </Stack>
            <Alert severity="warning" sx={{ mb: 2 }}>All COC requests must include supporting documentation. Upload relevant documents such as appraisals, borrower communications, fee invoices, or other evidence.</Alert>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Button variant="contained" component="label" startIcon={<UploadIcon />} disabled={uploadingFiles}>
                Upload Documents
                <input type="file" hidden multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileUpload} disabled={uploadingFiles} />
              </Button>
              {uploadingFiles && <CircularProgress size={20} />}
            </Stack>
            {formData.supportingDocuments.length > 0 && (
              <Stack spacing={1}>
                {formData.supportingDocuments.map(doc => (
                  <Stack key={doc.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                    <DescriptionIcon color="primary" fontSize="small" />
                    <Box sx={{ flex: 1 }}><Typography variant="body2" fontWeight={700}>{doc.fileName}</Typography><Typography variant="caption" color="text.secondary">{doc.fileSize}</Typography></Box>
                    <MuiTextField size="small" placeholder="Label (e.g., Appraisal)" value={doc.label} onChange={(e) => handleDocumentLabelChange(doc.id, e.target.value)} sx={{ width: 200 }} />
                    <IconButton size="small" color="error" onClick={() => handleRemoveDocument(doc.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
            {errors.supportingDocuments && <Typography variant="caption" color="error.main" sx={{ mt: 0.5 }}>{errors.supportingDocuments}</Typography>}
          </Box>
        )}

        {/* Step 5 */}
        {formData.category && formData.supportingDocuments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip label="5" size="small" color="primary" /><Typography variant="subtitle2">Broker Attestation *</Typography>
            </Stack>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Compliance Attestation Required</Typography>
              <Typography variant="body2" color="text.secondary">By checking the box below, you are attesting under penalty of law that this COC request is valid, compliant with TRID regulations, and supported by the attached documentation.</Typography>
            </Alert>
            <FormControlLabel control={<Checkbox checked={formData.attestation} onChange={(e) => { setFormData({ ...formData, attestation: e.target.checked }); setErrors({ ...errors, attestation: '' }); }} />} label={<Typography variant="body2"><strong>I attest that this change represents a valid Change of Circumstance under TRID regulations, was not known or reasonably foreseeable at the time of initial disclosure, and is supported by the attached documentation.</strong></Typography>} />
            {errors.attestation && <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>{errors.attestation}</Typography>}
          </Box>
        )}
      </CardContent>

      <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmit} disabled={!formData.attestation}>Submit COC Request</Button>
      </Stack>
    </Card>
  );
};

export default COCRequestForm;
